import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, ExternalLink, ArrowRight, ArrowLeft, Loader2, AlertCircle } from "lucide-react";

const curSymbol = (c) => (c === "USD" ? "$" : c === "GBP" ? "£" : c === "EUR" ? "€" : c || "");

export default function QuickAdd() {
    const [params] = useSearchParams();
    const { user, loading: authLoading } = useAuth();
    const nav = useNavigate();
    const [state, setState] = useState("loading"); // loading | preview | added | error
    const [item, setItem] = useState(null);
    const [errorMsg, setErrorMsg] = useState("");
    const [priceOverride, setPriceOverride] = useState("");

    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            // Not signed in — redirect to login preserving the current query
            const back = encodeURIComponent(window.location.pathname + window.location.search);
            nav(`/login?next=${back}`, { replace: true });
            return;
        }
        const url = (params.get("url") || "").trim();
        if (!url) { setErrorMsg("No URL provided"); setState("error"); return; }

        // Params-first (bookmarklet/share provided them)
        const paramTitle = (params.get("title") || "").trim();
        const paramPrice = parseFloat(params.get("price") || "");
        const paramCurrency = (params.get("currency") || "USD").toUpperCase();
        const paramImage = (params.get("image") || "").trim();
        const paramSource = (params.get("source") || "").trim();

        if (paramTitle && !Number.isNaN(paramPrice)) {
            setItem({ url, title: paramTitle, price: paramPrice, currency: paramCurrency, image_url: paramImage, source: paramSource });
            setState("preview");
            return;
        }

        // Fallback: server-side scrape
        (async () => {
            try {
                const { data } = await api.post("/wishlist/scrape-url", { url });
                setItem({
                    url: data.url,
                    title: paramTitle || data.title || "",
                    price: !Number.isNaN(paramPrice) ? paramPrice : data.price,
                    currency: paramCurrency || data.currency || "USD",
                    image_url: paramImage || data.image_url || "",
                    source: paramSource || data.source || "",
                });
                setState("preview");
            } catch (e) {
                setErrorMsg(e?.response?.data?.detail || "Couldn't read that page");
                setState("error");
            }
        })();
    }, [authLoading, user, params, nav]);

    const track = async () => {
        if (!item) return;
        const finalPrice = priceOverride ? parseFloat(priceOverride) : item.price;
        if (!finalPrice || Number.isNaN(finalPrice)) { toast.error("Enter a price first"); return; }
        if (!item.title) { toast.error("Enter a title first"); return; }
        try {
            await api.post("/wishlist/add", { ...item, price: finalPrice });
            setState("added");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Add failed");
        }
    };

    return (
        <div className="min-h-screen bg-neutral-50 flex flex-col">
            <header className="border-b border-neutral-200 bg-white">
                <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
                    <Link to="/wishlist" className="border border-neutral-300 h-9 w-9 flex items-center justify-center hover:border-black" data-testid="quick-back">
                        <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                    </Link>
                    <div className="text-center">
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">Price Radar</div>
                        <div className="font-display text-lg leading-none mt-0.5">Quick Add</div>
                    </div>
                    <div className="w-9" />
                </div>
            </header>

            <main className="flex-1 max-w-md w-full mx-auto p-4">
                {state === "loading" && (
                    <div className="flex flex-col items-center justify-center py-16 text-neutral-500 gap-2 font-body text-sm" data-testid="quick-loading">
                        <Loader2 className="h-6 w-6 animate-spin" strokeWidth={1.5} />
                        Reading the page…
                    </div>
                )}

                {state === "error" && (
                    <div className="border border-red-200 bg-red-50 p-4 mt-6" data-testid="quick-error">
                        <div className="flex gap-2 items-start">
                            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" strokeWidth={1.5} />
                            <div>
                                <div className="font-semibold text-red-800 text-sm">Couldn't add</div>
                                <div className="text-sm text-red-700 mt-1 font-body">{errorMsg}</div>
                            </div>
                        </div>
                        <Link to="/wishlist" className="mt-4 inline-block text-[10px] uppercase tracking-widest border border-black px-4 h-9 leading-[2.25rem] hover:bg-black hover:text-white">Back to Radar</Link>
                    </div>
                )}

                {state === "preview" && item && (
                    <div className="mt-4 bg-white border border-neutral-200" data-testid="quick-preview">
                        {item.image_url ? (
                            <img src={item.image_url} alt="" className="w-full aspect-[4/5] object-cover border-b border-neutral-200" loading="lazy" />
                        ) : (
                            <div className="w-full aspect-[4/5] flex items-center justify-center text-neutral-300 text-6xl font-display bg-neutral-50 border-b border-neutral-200">?</div>
                        )}
                        <div className="p-4">
                            <div className="text-[10px] uppercase tracking-widest text-neutral-500">{item.source}</div>
                            <div className="font-body text-sm mt-1 line-clamp-3">{item.title || "(no title — type one below)"}</div>
                            {item.price ? (
                                <div className="font-display text-3xl tabular-nums mt-3">{curSymbol(item.currency)}{Number(item.price).toFixed(2)}</div>
                            ) : (
                                <div className="mt-3">
                                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Price wasn't on the page — type it now</div>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={priceOverride}
                                        onChange={(e) => setPriceOverride(e.target.value)}
                                        placeholder="e.g. 79.99"
                                        className="w-full border border-neutral-300 h-11 px-3 mt-2 font-body text-lg tabular-nums"
                                        autoFocus
                                        data-testid="quick-price-input"
                                    />
                                </div>
                            )}
                            {!item.title && (
                                <input
                                    value={item.title}
                                    onChange={(e) => setItem({ ...item, title: e.target.value })}
                                    placeholder="Product title"
                                    className="w-full border border-neutral-300 h-10 px-3 mt-3 font-body text-sm"
                                    data-testid="quick-title-input"
                                />
                            )}
                            <button onClick={track} className="w-full mt-4 bg-black text-white h-12 uppercase tracking-widest text-[11px] font-semibold hover:bg-neutral-800 flex items-center justify-center gap-2" data-testid="quick-track-btn">
                                Track this price <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                            </button>
                            <a href={item.url} target="_blank" rel="noopener noreferrer" className="w-full mt-2 border border-neutral-300 h-10 flex items-center justify-center gap-2 text-[10px] uppercase tracking-widest hover:border-black">
                                <ExternalLink className="h-3 w-3" strokeWidth={1.5} /> Open on {item.source}
                            </a>
                        </div>
                    </div>
                )}

                {state === "added" && item && (
                    <div className="mt-8 text-center" data-testid="quick-added">
                        <div className="w-16 h-16 mx-auto rounded-full bg-green-100 border border-green-300 flex items-center justify-center">
                            <Check className="h-8 w-8 text-green-700" strokeWidth={2} />
                        </div>
                        <div className="font-display text-3xl mt-4">On the Radar</div>
                        <div className="text-sm text-neutral-500 font-body mt-2 leading-relaxed">
                            We'll ping you when <span className="font-semibold text-black">{item.title.slice(0, 40)}</span> drops below <span className="font-semibold text-black tabular-nums">{curSymbol(item.currency)}{Number(item.price).toFixed(2)}</span>.
                        </div>
                        <div className="flex gap-2 mt-6 justify-center">
                            <Link to="/wishlist" className="border border-black px-4 h-10 uppercase tracking-widest text-[10px] font-semibold leading-[2.5rem] hover:bg-black hover:text-white">Open Radar</Link>
                            <Link to="/deals" className="bg-yellow-300 border border-black text-black px-4 h-10 uppercase tracking-widest text-[10px] font-semibold leading-[2.5rem] hover:bg-yellow-400">See deals</Link>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
